import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { useLocale } from '@hooks/useLocale';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import { CheckCircleIcon, ProhibitIcon, XIcon } from '@phosphor-icons/react';

import Modal from '@components/global/Modal';
import DropDown from '_components/UI/DropDown';
import IconButton from '_components/UI/IconButton';
import SimpleButton from '_components/UI/SimpleButton';
import IconWrapper from '@components/ui/Icons';
import SimpleLink from '_components/UI/SimpleLink';
import { usePdp } from '@context/PdpContext';
import { StoreWithDistance, useStoresUI } from '@context/StoresContext';
import { useUI } from '@context/GlobalUIContext';
import { filterStoresForPdpPreview } from '../filterStoresForPdpPreview';
import { getStockTierFromQuantity } from '../getStockTier';
import { sortPreviewStoresByShopperGeolocation } from '../sortPreviewStoresByShopperGeolocation';
import { isClosedStoreHourValue, weeklyHours } from '@utils/store';
import { trackStoreAvailabilityModalChangeStoreEvent } from '@utils/gtm/ga4/trackStoreAvailabilityModalEvents';

import styles from './StoreInformationModal.module.scss';

const PRODUCT_PLACEHOLDER_IMAGE = '/images/pdp/product-placeholder.jpg';

type StockCellData = {
  quantity: number | null;
  label: string;
  srPrefix?: string;
  tier: ReturnType<typeof getStockTierFromQuantity>;
};

const formatTodayHours = (
  store: StoreWithDistance,
  tCommon: (key: string, opts?: Record<string, string>) => string
) => {
  const ordered = weeklyHours(
    store.data.store_hours,
    store.data.store_hour_exceptions
  );
  const today = ordered[0];
  if (!today?.open || !today?.close) {
    return tCommon('closed.today');
  }
  const open = today.open.trim();
  const close = today.close.trim();
  if (isClosedStoreHourValue(open) || isClosedStoreHourValue(close)) {
    return tCommon('closed.today');
  }
  return tCommon('stores.openToday', { open, close });
};

const variantLookupKey = (
  sizeId: number | null | undefined,
  colourId: number | null | undefined
) => `${sizeId ?? ''}:${colourId ?? ''}`;

type GridSizeRow = {
  rowKey: string;
  sizeId: number | null;
  name: string;
};

const getStatusCellData = (
  quantity: number | null,
  t: (key: string, opts?: Record<string, string | number>) => string
): StockCellData => {
  const tier = getStockTierFromQuantity(quantity);
  const count = quantity ?? 0;

  switch (tier) {
    case 'in_stock':
      return {
        quantity,
        tier,
        label: t('product.findInStore.inStockCount', { count }),
      };
    case 'few_left':
      return {
        quantity,
        tier,
        label: t('product.findInStore.fewLeftCount', { count }),
        srPrefix: t('product.findInStore.srFewLeft', { count }),
      };
    case 'out_of_stock':
      return {
        quantity,
        tier,
        label: t('product.findInStore.outOfStock'),
        srPrefix: t('product.findInStore.srOutOfStock'),
      };
    default:
      return {
        quantity,
        tier,
        label: t('product.findInStore.outOfStock'),
        srPrefix: t('product.findInStore.srOutOfStock'),
      };
  }
};

const StoreInformationModal = () => {
  const { t } = useTranslation('pdp');
  const { t: tCommon } = useTranslation('common');
  const locale = useLocale();
  const { displayStoreInformationModal, closeStoreInformationModal } = useUI();
  const { stores, favouriteStoreDetails } = useStoresUI();
  const { shopperGeolocation, sizes, colours, variants } = usePdp();
  const headingId = 'store-information-modal-heading';
  const [isStoreListOpen, setIsStoreListOpen] = useState(false);

  const favouriteForPreview =
    favouriteStoreDetails && favouriteStoreDetails !== false
      ? favouriteStoreDetails
      : undefined;

  const previewStores = useMemo(
    () => filterStoresForPdpPreview(stores, favouriteForPreview),
    [stores, favouriteForPreview]
  );

  const sortedStores = useMemo(() => {
    const sortedPreviewStores = sortPreviewStoresByShopperGeolocation(
      previewStores,
      shopperGeolocation
    );
    const sortedAllStores = sortPreviewStoresByShopperGeolocation(
      stores ?? [],
      shopperGeolocation
    );
    const previewStoreIds = new Set(
      sortedPreviewStores.map((store) => store.data.mec_store_id)
    );

    const remainingStores = sortedAllStores.filter(
      (store) => !previewStoreIds.has(store.data.mec_store_id)
    );

    return [...sortedPreviewStores, ...remainingStores];
  }, [previewStores, shopperGeolocation, stores]);

  const showStoreDropdown = sortedStores.length > 1;

  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

  useEffect(() => {
    if (!displayStoreInformationModal) {
      setSelectedStoreId(null);
      setIsStoreListOpen(false);
    }
  }, [displayStoreInformationModal]);

  const selectedStore = useMemo(() => {
    if (!sortedStores.length) {
      return undefined;
    }
    const fromState = selectedStoreId
      ? sortedStores.find((s) => s.data.mec_store_id === selectedStoreId)
      : undefined;
    return fromState ?? sortedStores[0];
  }, [selectedStoreId, sortedStores]);

  const selectableStores = useMemo(
    () =>
      selectedStore
        ? sortedStores.filter(
            (store) =>
              store.data.mec_store_id !== selectedStore.data.mec_store_id
          )
        : sortedStores,
    [selectedStore, sortedStores]
  );

  const colourColumns = useMemo(
    () =>
      (colours ?? []).map((colour) => {
        const representativeVariant = (variants ?? []).find(
          (variant) => variant.colourId === colour.id
        );
        return {
          ...colour,
          imageSrc:
            representativeVariant?.defaultImage?.url ??
            representativeVariant?.defaultImage?.urlOriginal ??
            PRODUCT_PLACEHOLDER_IMAGE,
        };
      }),
    [colours, variants]
  );

  const gridSizeRows = useMemo((): GridSizeRow[] => {
    if (sizes?.length) {
      return sizes.map((size) => ({
        rowKey: String(size.id),
        sizeId: size.id,
        name: size.name ?? '',
      }));
    }
    if (colourColumns.length > 0 && (variants?.length ?? 0) > 0) {
      return [
        {
          rowKey: 'one-size',
          sizeId: null,
          name: t('product.findInStore.modal.grid.oneSize'),
        },
      ];
    }
    return [];
  }, [colourColumns, sizes, variants, t]);

  const variantsBySizeAndColour = useMemo(() => {
    const lookup = new Map<string, NonNullable<typeof variants>[number]>();
    (variants ?? []).forEach((variant) => {
      lookup.set(variantLookupKey(variant.sizeId, variant.colourId), variant);
    });
    return lookup;
  }, [variants]);

  return (
    <Modal
      labelledBy={headingId}
      onClose={closeStoreInformationModal}
      isOpen={displayStoreInformationModal}
      containerClassNames={styles.modalContainer}
      scrollableClassNames={styles.modalScrollable}
      fitContent
      bottomPositioning
      closeButtonVisible={false}
    >
      {!displayStoreInformationModal || !selectedStore ? null : (
        <div className={styles.root}>
          <div className={styles.intrinsicColumn}>
            <div className={styles.gridScroller}>
              <table className={styles.grid}>
                <thead>
                  <tr className={styles.headerRow}>
                    <th className={styles.colourHeaderFirst}>
                      <span className={styles.srOnly}>
                        {t('product.findInStore.modal.grid.sizeHeader')}
                      </span>
                    </th>
                    {colourColumns.map((colour) => (
                      <th key={colour.id} className={styles.colourHeader}>
                        <Image
                          src={colour.imageSrc}
                          alt={colour.name}
                          width={72}
                          height={72}
                          className={styles.colourImage}
                        />
                        <div className={styles.colourLabel}>{colour.name}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gridSizeRows.map((sizeRow, rowIndex) => (
                    <tr
                      key={sizeRow.rowKey}
                      className={cn({
                        [styles.altRow]: rowIndex % 2 === 1,
                      })}
                    >
                      <th scope="row" className={styles.sizeCell}>
                        {sizeRow.name}
                      </th>
                      {colourColumns.map((colour) => {
                        const variant = variantsBySizeAndColour.get(
                          variantLookupKey(sizeRow.sizeId, colour.id)
                        );
                        const stock =
                          variant?.storeInventory?.[
                            selectedStore.data.mec_store_id
                          ];
                        const stockCell = getStatusCellData(
                          stock?.quantity ?? null,
                          t
                        );

                        return (
                          <td
                            key={`${sizeRow.rowKey}-${colour.id}`}
                            className={styles.stockCell}
                          >
                            <div className={styles.stockCellContent}>
                              {stockCell.tier === 'in_stock' ? (
                                <CheckCircleIcon
                                  size={20}
                                  weight="fill"
                                  color="var(--primary-500)"
                                  aria-hidden="true"
                                />
                              ) : null}
                              {stockCell.tier === 'few_left' ? (
                                <IconWrapper
                                  icon="fire"
                                  width={20}
                                  height={20}
                                  fill="var(--accents-popular-500)"
                                  classNames={styles.stockIcon}
                                />
                              ) : null}
                              {stockCell.tier === 'out_of_stock' ||
                              stockCell.tier === 'unknown' ? (
                                <ProhibitIcon
                                  size={20}
                                  weight="bold"
                                  color="var(--accents-sale-500)"
                                  aria-hidden="true"
                                />
                              ) : null}
                              {stockCell.srPrefix ? (
                                <span className={styles.srOnly}>
                                  {stockCell.srPrefix}
                                </span>
                              ) : null}
                              <span>{stockCell.label}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.divider} />

            <div className={styles.headerContent}>
              <div className={styles.mobileCloseButton}>
                <IconButton
                  icon={XIcon}
                  type="transparent"
                  aria-label={t('close')}
                  onClick={() => closeStoreInformationModal()}
                  buttonType="button"
                  iconSize={20}
                  size="sm"
                  iconWeight="bold"
                />
              </div>
              <div className={styles.headingRow}>
                <h2 id={headingId} className={styles.heading}>
                  {t('product.findInStore.modal.title', {
                    storeName: selectedStore.data.store_name,
                  })}
                </h2>
                <div className={styles.desktopCloseButton}>
                  <IconButton
                    icon={XIcon}
                    type="outline"
                    aria-label={t('close')}
                    onClick={() => closeStoreInformationModal()}
                    buttonType="button"
                    iconSize={16}
                    size="xl"
                    iconWeight="bold"
                    className={styles.desktopCloseButtonIcon}
                  />
                </div>
              </div>

              <div className={styles.storeSelectorContainer}>
                <div className={styles.storeSelectorWrap}>
                  {showStoreDropdown ? (
                    <DropDown.Root
                      open={isStoreListOpen}
                      onOpenChange={setIsStoreListOpen}
                      stackAboveDialog
                    >
                      <DropDown.Trigger
                        variant="secondary"
                        className={styles.storeSelectorLabel}
                      >
                        {selectedStore.data.store_name}
                      </DropDown.Trigger>
                      <DropDown.Content>
                        <ul
                          id="store-information-modal-store-list"
                          className={styles.storeOptionList}
                        >
                          {selectableStores.map((store) => (
                            <li key={store.data.mec_store_id}>
                              <SimpleButton
                                type="button"
                                variant="secondary"
                                size="small"
                                className={styles.storeOption}
                                onClick={() => {
                                  trackStoreAvailabilityModalChangeStoreEvent({
                                    newlySelectedStore: store.data.store_name,
                                    previouslySelectedStore:
                                      selectedStore.data.store_name,
                                  });
                                  setSelectedStoreId(store.data.mec_store_id);
                                  setIsStoreListOpen(false);
                                }}
                              >
                                {store.data.store_name}
                              </SimpleButton>
                            </li>
                          ))}
                        </ul>
                      </DropDown.Content>
                    </DropDown.Root>
                  ) : (
                    <span className={styles.storeSelectorLabel}>
                      {selectedStore.data.store_name}
                    </span>
                  )}
                </div>

                <div className={styles.storeInformationContainer}>
                  <SimpleLink
                    className={styles.addressLink}
                    variant="secondary"
                    href={`/${locale ?? 'en'}/stores/${selectedStore.uid}`}
                  >
                    {selectedStore.data.address_line_1}
                  </SimpleLink>
                  <p className={styles.openHours}>
                    {formatTodayHours(selectedStore, tCommon)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default StoreInformationModal;
